import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/utils/toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { getApiUrl } from "@/utils/api";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sector, Country } from "@/types";

// Define API response interface
interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}
import { Switch } from "@/components/ui/switch";

interface CompaniesFormProps {
  companyId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const companySchema = z.object({
  name: z.string().min(1, "Name is required"),
  fiscal_name: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  fiscal_email: z
    .string()
    .email("Invalid email")
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val))
    .or(z.literal("")),
  vat: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  address: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  cp: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  city: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  province: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  country: z.number().optional().nullable(),
  sector: z.number().optional().nullable(),
  phone: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  mobile: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  email: z.string().email("Invalid email"),
  logo: z.any().optional(),
  is_active: z.boolean(),
  has_vehicles: z.boolean().default(false),
});

type CompanyFormValues = z.infer<typeof companySchema>;

export default function CompaniesForm({
  companyId: propCompanyId,
  onSuccess,
  onCancel,
}: CompaniesFormProps): ReactElement {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [sector, setSector] = useState<Sector | null>(null);

  // Get companyId from navigation state if available
  const stateCompanyId = location.state?.companyId;

  // Use the companyId from props or from navigation state
  const companyId = propCompanyId || stateCompanyId;

  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: "",
      fiscal_name: "",
      fiscal_email: "",
      vat: "",
      address: "",
      cp: "",
      city: "",
      province: "",
      country: undefined,
      sector: undefined,
      phone: "",
      mobile: "",
      email: "",
      logo: "",
      is_active: true,
      has_vehicles: false,
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["company", companyId],
    queryFn: async () => {
      if (!companyId) return null;
      const response = await fetch(getApiUrl(`companies/${companyId}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch company");
      }
      return response.json();
    },
    enabled: !!companyId,
  });

  // Query to fetch sectors
  const { data: sectors } = useQuery({
    queryKey: ["sectors"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("sectors/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch sectors");
      }
      const sectors = await response.json();
      return sectors?.results || [];
    },
  });

  // Query to fetch countries
  const { data: countriesData, isLoading: isLoadingCountries } = useQuery<
    ApiResponse<Country>
  >({
    queryKey: ["countries"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("countries/?limit=9999"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch countries");
      }
      const countries = await response.json();
      return countries;
    },
    enabled: !!token,
  });

  const countries = countriesData?.results || [];

  useEffect(() => {
    if (data) {
      form.reset({
        ...data,
        country: data.country, // Assuming data.country is the ID itself
        sector: data.sector?.id,
      });

      // Set sector state when data is loaded
      if (data.sector?.id && sectors?.length) {
        const foundSector = sectors.find(
          (s: Sector) => s.id === data.sector.id,
        );
        setSector(foundSector || null);
      }
    }
  }, [data, form, sectors]);

  const mutation = useMutation({
    mutationFn: async (values: CompanyFormValues) => {
      const url = companyId
        ? getApiUrl(`companies/${companyId}/`)
        : getApiUrl("companies/");
      const method = companyId ? "PATCH" : "POST";

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);
      if (values.fiscal_name) {
        formData.append("fiscal_name", values.fiscal_name);
      }
      if (values.fiscal_email) {
        formData.append("fiscal_email", values.fiscal_email);
      }
      if (values.vat) {
        formData.append("vat", values.vat);
      }
      if (values.address) {
        formData.append("address", values.address);
      }
      if (values.cp) {
        formData.append("cp", values.cp);
      }
      if (values.city) {
        formData.append("city", values.city);
      }
      if (values.province) {
        formData.append("province", values.province);
      }
      if (values.country) {
        formData.append("country", values.country.toString());
      }
      if (values.sector) {
        formData.append("sector", values.sector.toString());
      }
      if (values.phone) {
        formData.append("phone", values.phone);
      }
      if (values.mobile) {
        formData.append("mobile", values.mobile);
      }
      formData.append("is_active", values.is_active.toString());
      // Solo agregar el logo si es un archivo válido
      if (values.logo instanceof File) {
        formData.append("logo", values.logo);
      } else if (
        values.logo &&
        typeof values.logo === "string" &&
        !values.logo.startsWith("http")
      ) {
        // Handle base64 or other string format that isn't a URL
        formData.append("logo", values.logo);
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save company");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast.success(
        `Company ${companyId ? "updated" : "created"} successfully`,
      );
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (values: CompanyFormValues) => {
    mutation.mutate(values);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Form {...form}>
      {stateCompanyId && (
        <div className="sm:flex sm:items-center mb-8">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold">{t("common.my-company")}</h1>
          </div>
        </div>
      )}

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full pb-24 overflow-y-auto"
      >
        <div className="space-y-6 overflow-y-auto flex-1 px-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.name")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.email")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    {...field}
                    readOnly={!!companyId}
                    className={companyId ? "bg-muted" : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vat"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.vat")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sector"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.sector", "Sector")}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value ? parseInt(value) : null);
                      const sector = sectors?.find(
                        (s: Sector) => s.id === parseInt(value),
                      );
                      setSector(sector);
                    }}
                    value={field.value?.toString() || ""}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {sectors?.map((sector: Sector) => (
                        <SelectItem
                          key={sector.id}
                          value={sector.id.toString()}
                        >
                          {t(sector.name)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {sector?.code === "H" ? (
            <FormField
              control={form.control}
              name="has_vehicles"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <FormLabel className="mr-4">
                    {t("companies.fields.has_vehicles")}
                  </FormLabel>
                  <FormControl>
                    <Switch
                      onCheckedChange={(checked) => field.onChange(checked)}
                      checked={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}

          <FormField
            control={form.control}
            name="fiscal_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.fiscal_name")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fiscal_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.fiscal_email")}</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.phone")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.address")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cp"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.cp")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.city")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="province"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.province")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.country")}</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(Number(value))}
                  value={field.value?.toString()}
                  disabled={isLoadingCountries}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("companies.fields.country")}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {isLoadingCountries ? (
                      <SelectItem value="loading" disabled>
                        {t("companies.fields.loading_countries")}
                      </SelectItem>
                    ) : (
                      countries.map((country) => (
                        <SelectItem
                          key={country.id}
                          value={country.id.toString()}
                        >
                          {country.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="mobile"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="logo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Logo</FormLabel>
                <FormControl>
                  <div className="flex flex-col gap-2">
                    {data?.logo && (
                      <img
                        src={data.logo}
                        alt="Receipt preview"
                        className="w-32 h-32 object-cover rounded"
                      />
                    )}
                    <Input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        field.onChange(file || null);
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => field.onChange(value === "true")}
                    value={field.value ? "true" : "false"}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="fixed inset-x-0 bottom-0 bg-background p-4 border-t flex justify-end space-x-2 mt-auto">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="default" type="submit">
            {companyId ? t("common.update") : t("common.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
